/* eslint-disable react/prop-types */

import { subject } from '@casl/ability'
import { colors } from '@lmc/common/constants'
import { UserRole } from '@lmc/ts-common'
import { Button, Icon } from '@lmc/ui-kit'
import { useAbilityAccess } from 'components/AccessControl'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { getJSON } from '../../../common/services/api'
import { ProfileLink } from '../../../components/generic'
import { EmptyAvatars, HomeCard } from './index'

const TITLE = 'Family App'

type Profile = any

type Data = {
  result: {
    activeAccounts: any
    seenRecently: unknown[]
    invited: boolean
  }
}

type Response = {
  data: Data
}

const getBodyText = (seenRecently: number, invited: boolean) => {
  if (!invited)
    return <p>You`re all set up, but no family members have been invited yet</p>
  if (!seenRecently)
    return (
      <p>
        It doesn’t look like any family members have been active recently (last
        24 hrs)
      </p>
    )
  if (seenRecently === 1) {
    return (
      <p>
        <strong>One family member</strong> has been active recently (last 24h)
      </p>
    )
  }
  return (
    <p>
      <strong>{seenRecently} family members</strong> have been active recently
      (last 24h)
    </p>
  )
}

const BodySkeleton = () => (
  <SkeletonWrapper>
    <SkeletonLine $width="200px" />
    <SkeletonLine $width="280px" />
    <EmptyAvatars />
  </SkeletonWrapper>
)

const Body = ({ data }: Response) => {
  const [nShowing, showMore] = useState(10)
  if (!data) return <BodySkeleton />
  return (
    <div>
      <p style={{ marginBlock: 0 }}>
        Active <StyledCount>({data?.result?.activeAccounts || 0})</StyledCount>
        Sent Invite <StyledCount>({data?.result?.invited || 0})</StyledCount>
      </p>
      {getBodyText(data?.result?.seenRecently?.length, data?.result?.invited)}
      <div className="lmc-flex-grid">
        {_.take(data?.result?.seenRecently || [], nShowing).map(
          (profile: Profile) => (
            <ProfileLink
              key={profile.id}
              list="residents"
              id={profile.resident}
              name={profile.name}
              picture={profile.picture}
            />
          )
        )}
        {data?.result?.seenRecently?.length > nShowing ? (
          <button
            type="button"
            className="link-button lmc-more-link"
            onClick={() => showMore(nShowing + 30)}
          >
            More...
          </button>
        ) : null}
      </div>
    </div>
  )
}

const FamilyCard = () => {
  const [data, setData] = useState()

  const canInviteFamily = useAbilityAccess([
    { i: 'create', this: subject('UserInvitation', { role: UserRole.family }) }
  ])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    getJSON({ url: `/api/list/carers/family-status` }).then(setData)
  }, [])

  const inviteButton = (
    <Button
      variant="primary"
      icon={<Icon name="MailOutlined" />}
      disabledByPermission={!canInviteFamily}
    >
      Invite a Family Member
    </Button>
  )

  return (
    <HomeCard
      title={TITLE}
      footer={
        <StyledFooter>
          {canInviteFamily ? (
            <Link to="/settings/family-invite">{inviteButton}</Link>
          ) : (
            inviteButton
          )}
        </StyledFooter>
      }
    >
      <Body data={data as unknown as Data} />
    </HomeCard>
  )
}

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`

const StyledCount = styled.span`
  margin-right: 12px;
  color: ${colors.darkgrey};
`

const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 4px;
`

const SkeletonLine = styled.div<{ $width: string }>`
  width: ${({ $width }) => $width};
  height: 14px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.color.neutral.color8};
`

export default FamilyCard
